import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type BackendBankAccount = {
	id: string;
	orgId: string;
	accountNumber: string;
	bankName: string;
	bankMfo: string | null;
	accountName: string | null;
	createdAt: string;
	updatedAt: string;
	isDeleted: boolean;
};

export type FrontendBankAccount = {
	id?: string;
	accountNumber: string;
	bankName: string;
	mfo: string;
	accountName: string;
};

export type SaveBankAccountPayload = {
	accountNumber: string;
	bankName: string;
	bankMfo?: string;
	accountName?: string;
};

function mapBackendToFrontend(backend: BackendBankAccount): FrontendBankAccount {
	return {
		id: backend.id,
		accountNumber: backend.accountNumber,
		bankName: backend.bankName,
		mfo: backend.bankMfo ?? "",
		accountName: backend.accountName ?? "",
	};
}

function mapFrontendToSave(frontend: FrontendBankAccount): SaveBankAccountPayload {
	return {
		accountNumber: frontend.accountNumber,
		bankName: frontend.bankName,
		...(frontend.mfo ? { bankMfo: frontend.mfo } : {}),
		...(frontend.accountName ? { accountName: frontend.accountName } : {}),
	};
}

export async function getBankAccount(): Promise<FrontendBankAccount | null> {
	const response = await apiClient
		.get<{ data: BackendBankAccount[] }>(endpoints.payments.bankAccounts)
		.catch((error) => {
			console.error("[BankAccountsApi] Failed to fetch bank account:", error);
			return { data: { data: [] as BackendBankAccount[] } };
		});
	const list = response.data.data;
	if (!list || list.length === 0) {
		return null;
	}
	return mapBackendToFrontend(list[0]);
}

export async function saveBankAccount(account: FrontendBankAccount): Promise<FrontendBankAccount> {
	if (account.id) {
		const response = await apiClient
			.patch<{ data: BackendBankAccount }>(
				endpoints.payments.bankAccountDetail(account.id),
				mapFrontendToSave(account)
			)
			.catch((error) => {
				console.error("[BankAccountsApi] Failed to save bank account:", error);
				throw error;
			});
		return mapBackendToFrontend(response.data.data);
	}

	const response = await apiClient
		.post<{ data: BackendBankAccount }>(endpoints.payments.bankAccounts, mapFrontendToSave(account))
		.catch((error) => {
			console.error("[BankAccountsApi] Failed to create bank account:", error);
			throw error;
		});
	return mapBackendToFrontend(response.data.data);
}
